import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { UMR } from '../../urbanmap/_components';
import { CreateProjectDialog } from './CreateProjectDialog';
import { usePlanBootstrap, useScenarios } from './hooks';
import { DEFAULT_VISIBILITY, type PlanLayerVisibility } from './mapLayers';
import { DEFAULT_HYPOTHESES } from './mocks';
import { PlanHeader } from './PlanHeader';
import { PlanMapView, type ScenarioOverlay } from './PlanMapView';
import { StudyView } from './StudyView';
import type { PlanProject, PlanSpace, Stage } from './types';
import { Toast } from './ui';
import { UrbanMapEntry } from './UrbanMapEntry';

export const meta = {
  title: 'Plan — Proposition C (Carte | Étude)',
  description:
    'De UrbanMap à Plan : création d’une copie de travail figée d’un périmètre UM, puis deux espaces — Carte (données du projet modifiables + couches UM en direct) et Étude (scénarios avec hypothèses propres, calcul, comparaison).',
  tags: ['planning', 'network', 'poc'],
  author: 'plan-team',
  status: 'draft' as const,
  createdAt: '2026-10-14',
  updatedAt: '2026-10-20',
};

const STUDIO_HEADER = 48;

export default function PlanPropositionC() {
  const { data, isLoading, error } = usePlanBootstrap('pl-lyon-2');
  const { scenarios, updateHypotheses, run, duplicate, create, rename } = useScenarios(data?.scenarios);

  const [stage, setStage] = useState<Stage>('urbanmap');
  const [space, setSpace] = useState<PlanSpace>('carte');
  const [project, setProject] = useState<PlanProject | null>(null);
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [visibility, setVisibility] = useState<PlanLayerVisibility>(DEFAULT_VISIBILITY);
  const [selectedId, setSelectedId] = useState('sc-1');
  const [overlayId, setOverlayId] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (document.querySelector(`link[href="${UMR.fontHref}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = UMR.fontHref;
    document.head.appendChild(link);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  const overlay: ScenarioOverlay | null = useMemo(() => {
    const s = scenarios.find((x) => x.id === overlayId);
    return s?.results ? { id: s.id, name: s.name, connected: new Set(s.results.connectedBuildings) } : null;
  }, [scenarios, overlayId]);

  const openProject = (p: PlanProject) => {
    setProject(p);
    setStage('plan');
    setSpace('carte');
  };

  let body: JSX.Element;
  if (isLoading || !data) {
    body = <div style={{ padding: 24, fontFamily: UMR.font }}>{error ? 'Erreur de chargement du projet.' : 'Chargement du projet…'}</div>;
  } else if (stage !== 'plan' || !project) {
    body = (
      <div style={{ position: 'relative', height: '100%' }}>
        <UrbanMapEntry data={data} onCreate={() => setStage('creating')} onOpenExisting={() => openProject(data.project)} />
        {stage === 'creating' && (
          <CreateProjectDialog
            data={data}
            onCancel={() => setStage('urbanmap')}
            onCreated={(name) => {
              openProject({ ...data.project, name });
              setOnboarding(true);
              flash('Copie de travail créée — UrbanMap n’est pas modifié');
            }}
          />
        )}
      </div>
    );
  } else {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PlanHeader
          data={data}
          project={project}
          space={space}
          onSpaceChange={setSpace}
          modifiedCount={Object.keys(overrides).length}
          driftShown={visibility.drift}
          onShowDrift={() => { setVisibility((v) => ({ ...v, drift: !v.drift })); setSpace('carte'); }}
          onOpenUM={() => { setStage('urbanmap'); flash('Retour à UrbanMap (données en direct) — le projet reste disponible'); }}
          onDuplicate={() => flash(`« ${project.name} (copie) » créé : données, modifications et scénarios dupliqués (mock)`)}
          onShare={() => flash('Lien de partage copié — les invités voient la même copie de travail (mock)')}
          onNewFromUM={() => setStage('creating')}
          onSwitchProject={(name) => flash(`Ouverture du projet « ${name} » (mock)`)}
        />
        {space === 'carte' ? (
          <PlanMapView
            data={data}
            project={project}
            overrides={overrides}
            onOverride={(fid, heat) => setOverrides((o) => {
              const next = { ...o };
              if (heat === null) delete next[fid];
              else next[fid] = heat;
              return next;
            })}
            visibility={visibility}
            onVisibilityChange={setVisibility}
            scenario={overlay}
            onClearScenario={() => setOverlayId(null)}
            onBackToStudy={() => setSpace('etude')}
            flash={flash}
            showOnboarding={onboarding}
            onDismissOnboarding={() => setOnboarding(false)}
          />
        ) : (
          <StudyView
            data={data}
            project={project}
            modifiedCount={Object.keys(overrides).length}
            scenarios={scenarios}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onUpdate={updateHypotheses}
            onRun={run}
            onDuplicate={(id) => { setSelectedId(duplicate(id)); flash('Scénario dupliqué avec ses hypothèses'); }}
            onCreate={() => setSelectedId(create(DEFAULT_HYPOTHESES))}
            onRename={rename}
            onShowOnMap={(id) => { setOverlayId(id); setSpace('carte'); }}
            onGoToMap={() => setSpace('carte')}
          />
        )}
      </div>
    );
  }

  return (
    <PrototypeStudioLayout title={meta.title}>
      <div style={{ position: 'relative', height: `calc(100vh - ${STUDIO_HEADER}px)`, overflow: 'hidden', fontFamily: UMR.font, background: UMR.white }}>
        {body}
        {toast && <Toast>{toast}</Toast>}
      </div>
    </PrototypeStudioLayout>
  );
}
